import { useQuery } from '@tanstack/react-query';
import { searchApi, SearchParams, SearchResult } from '@/services/api';
import type { FilterValues } from '@/components/property/PropertyFilters';

interface UsePropertySearchOptions {
  listingType: 'sale' | 'rent';
  filters: FilterValues;
  page: number;
  limit?: number;
  query?: string;
}

export function usePropertySearch({ listingType, filters, page, limit = 20, query }: UsePropertySearchOptions) {
  return useQuery({
    queryKey: ['properties', listingType, filters, page, limit, query],
    queryFn: async (): Promise<{ properties: any[]; total: number; totalPages: number }> => {
      const params: SearchParams = {
        listing_type: listingType,
        page: String(page),
        limit: String(limit),
      };

      if (query) params.q = query;
      if (filters.city) params.city = filters.city;
      if (filters.subCity) params.sub_city = filters.subCity;
      if (filters.propertyType) params.property_type = filters.propertyType;
      if (filters.minPrice) params.min_price = filters.minPrice;
      if (filters.maxPrice) params.max_price = filters.maxPrice;
      if (filters.minBedrooms) params.min_bedrooms = filters.minBedrooms;
      if (filters.maxBedrooms) params.max_bedrooms = filters.maxBedrooms;
      if (filters.minArea) params.min_area = filters.minArea;
      if (filters.maxArea) params.max_area = filters.maxArea;
      if (filters.isFurnished) params.is_furnished = 'true';
      if (filters.hasParking) params.has_parking = 'true';
      if (filters.sortBy && filters.sortBy !== 'newest') params.sort_by = filters.sortBy;

      const result: SearchResult = await searchApi.search(params);

      // Client-side advanced-filter pass for fields the search RPC doesn't accept yet.
      let properties = result.properties || [];
      const cutoffMs = filters.daysOnPlatform ? Date.now() - parseInt(filters.daysOnPlatform) * 86400_000 : null;
      properties = properties.filter((p: any) => {
        if (filters.isVerified && !p.is_verified) return false;
        if (filters.isSuperHot && !p.is_super_hot) return false;
        if (filters.occupancyStatus) {
          const v = p.occupancy_status || 'not_specified';
          if (v !== filters.occupancyStatus) return false;
        }
        if (filters.ownershipStatus) {
          const v = p.ownership_status || 'not_specified';
          if (v !== filters.ownershipStatus) return false;
        }
        if (filters.paymentTypes && filters.paymentTypes.length > 0) {
          const types: string[] = Array.isArray(p.payment_types) ? p.payment_types : [];
          if (!filters.paymentTypes.some(t => types.includes(t))) return false;
        }
        if (filters.listedByAgency && !p.agency_id) return false;
        if (cutoffMs && p.created_at && new Date(p.created_at).getTime() < cutoffMs) return false;
        return true;
      });

      return {
        properties,
        total: result.total || 0,
        totalPages: result.totalPages || 0,
      };
    },
  });
}
